/**
 * Running a local relay server will allow you to hide your API key
 * and run custom logic on the server
 *
 * Set the local relay server address to:
 * REACT_APP_LOCAL_RELAY_SERVER_URL=http://localhost:8081
 *
 * This will also require you to set OPENAI_API_KEY= in a `.env` file
 * You can run it with `npm run relay`, in parallel with `npm start`
 */
const LOCAL_RELAY_SERVER_URL = 'https://cgpt-rt-nbaa.jet.ai:8081';
const fakeJets = true;
const fakeMems = false;

import { useEffect, useRef, useCallback, useState, TouchEvent } from 'react';
import Modal from 'react-modal';

Modal.setAppElement(document.getElementById('root') as HTMLElement);
import React from 'react';

import { RealtimeClient } from '@openai/realtime-api-beta';
import { ItemType } from '@openai/realtime-api-beta/dist/lib/client.js';
import { WavRecorder, WavStreamPlayer } from '../lib/wavtools/index.js';
import { instructions } from '../utils/conversation_config.js';
import { WavRenderer } from '../utils/wav_renderer';

import { X, Edit, Zap, ArrowUp, ArrowDown } from 'react-feather';
import { formatText } from '../utils/textFormatter';
import { Button } from '../components/button/Button';
import { Toggle } from '../components/toggle/Toggle';
import { Map } from '../components/Map';

import './ConsolePage.scss';
import EmblaCarousel from '../components/EmblaCarousel';
import useEmblaCarousel from 'embla-carousel-react';

const OPTIONS = { loop: true, slidesToScroll: 1, slidesToShow: 1 };
const CARDS = [
  {
    image: "http://jetleg.ai/assets/models/hawker_900xp.jpg",
    model: "Hawker 900XP",
    duration: "55 mins",
    seats: "9",
    cost: "$13,850"
  },
  {
    image: "http://jetleg.ai/assets/models/learjet_60xr.jpg",
    model: "Learjet 60XR",
    duration: "51 mins",
    seats: "7",
    cost: "$15,400"
  },
  {
    image: "http://jetleg.ai/assets/models/hawker_800xp.jpg",
    model: "Hawker 800XP",
    duration: "52 mins",
    seats: "9",
    cost: "$15,500"
  },
  {
    image: "http://jetleg.ai/assets/models/gulfstream_g280.jpg",
    model: "Gulfstream G280",
    duration: "1 hr 10 mins",
    seats: "10",
    cost: "$25,000"
  }
];
import { localImages } from '../utils/remoteimages.js';

function formatCost(price: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(price);
}

function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours} hr ${mins} min`;
}

/**
 * Type for result from get_weather() function call
 */
/**
 * Type for result from get_weather() function call
 */
type Coordinates = {
  lat: number;
  lng: number;
  location?: string;
  temperature?: {
    value: number;
    units: string;
  };
  wind_speed?: {
    value: number;
    units: string;
  };
};

/**
 * Type for all event logs
 */
type RealtimeEvent = {
  time: string;
  source: 'client' | 'server';
  count?: number;
  event: { [key: string]: any };
};

export function ConsolePage() {
  /**
   * Handle touch start and end events for swipe detection
   */
  const touchStartXRef = useRef<number | null>(null);

  const handleTouchStart = (e: TouchEvent) => {
    touchStartXRef.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: TouchEvent) => {
    if (touchStartXRef.current !== null) {
      const touchEndX = e.changedTouches[0].clientX;
      const deltaX = touchStartXRef.current - touchEndX;
      // Snap to the nearest card if swipe is not significant
      const jetTiles = document.querySelector('.jet-tiles');
      if (jetTiles) {
        const scrollLeft = jetTiles.scrollLeft;
        const cardWidth = jetTiles.clientWidth;
        const nearestCard = Math.round(scrollLeft / cardWidth);
        jetTiles.scrollTo({
          left: nearestCard * cardWidth,
          behavior: 'smooth',
        });
      }
      touchStartXRef.current = null;
      setMemoryKv({
        name: "John",
        age: "40",
        location: "SF, CA",
        pets: "1 dog",
        common_to: "LA"
      });
    }
  };

  const handleScrollSnap = (e: Event) => {
    const jetTiles = document.querySelector('.jet-tiles');
    if (jetTiles) {
      const scrollLeft = jetTiles.scrollLeft;
      const cardWidth = jetTiles.clientWidth;
      const nearestCard = Math.round(scrollLeft / cardWidth);
      const targetScrollLeft = nearestCard * cardWidth;

      if (Math.abs(scrollLeft - targetScrollLeft) > 1) {
        jetTiles.scrollTo({
          left: targetScrollLeft,
          behavior: 'smooth',
        });
      }
    }
  };

  /**
   * Ask user for API Key
   * If we're using the local relay server, we don't need this
   */
  const apiKey = LOCAL_RELAY_SERVER_URL
    ? ''
    : localStorage.getItem('tmp::voice_api_key') ||
      prompt('OpenAI API Key') ||
      '';
  if (apiKey !== '') {
    localStorage.setItem('tmp::voice_api_key', apiKey);
  }

  /**
   * Instantiate:
   * - WavRecorder (speech input)
   * - WavStreamPlayer (speech output)
   * - RealtimeClient (API client)
   */
  const wavRecorderRef = useRef<WavRecorder>(
    new WavRecorder({ sampleRate: 24000 })
  );
  const wavStreamPlayerRef = useRef<WavStreamPlayer>(
    new WavStreamPlayer({ sampleRate: 24000 })
  );
  const clientRef = useRef<RealtimeClient>(
    new RealtimeClient(
      LOCAL_RELAY_SERVER_URL
        ? { url: LOCAL_RELAY_SERVER_URL }
        : {
            apiKey: apiKey,
            dangerouslyAllowAPIKeyInBrowser: true,
          }
    )
  );

  /**
   * References for
   * - Rendering audio visualization (canvas)
   * - Autoscrolling event logs
   * - Timing delta for event log displays
   */
  const clientCanvasRef = useRef<HTMLCanvasElement>(null);
  const serverCanvasRef = useRef<HTMLCanvasElement>(null);
  const eventsScrollHeightRef = useRef(0);
  const eventsScrollRef = useRef<HTMLDivElement>(null);
  const startTimeRef = useRef<string>(new Date().toISOString());

  /**
   * All of our variables for displaying application state
   * - items are all conversation items (dialog)
   * - realtimeEvents are event logs, which can be expanded
   * - memoryKv is for set_memory() function
   * - coords, marker are for get_weather() function
   */
  const [items, setItems] = useState<ItemType[]>([]);
  const [realtimeEvents, setRealtimeEvents] = useState<RealtimeEvent[]>([]);
  const [expandedEvents, setExpandedEvents] = useState<{
    [key: string]: boolean;
  }>({});
  const [isConnected, setIsConnected] = useState(false);
  const [canPushToTalk, setCanPushToTalk] = useState(true);
  const [isRecording, setIsRecording] = useState(false);
  const [memoryKv, setMemoryKv] = useState<{ [key: string]: any }>(() => {
    const storedMemory = localStorage.getItem('memoryKv');
    if (storedMemory && fakeMems) {
      return JSON.parse(storedMemory);
    } else {
      localStorage.removeItem('memoryKv');
      return {};
    }
  });
  const [isMemoryExpanded, setIsMemoryExpanded] = useState(false);
  const [phrasesExpanded, setPhrasesExpanded] = useState(true);
  const [coords, setCoords] = useState<Coordinates | null>({
    lat: 37.775593,
    lng: -122.418137,
  });
  const [marker, setMarker] = useState<Coordinates | null>(null);
  const [autoFlag, setAutoFlag] = useState<boolean>(localStorage.getItem('autoFlag') === 'true');
  const [vadFlag, setVadFlag] = useState<boolean>(localStorage.getItem('vadFlag') === 'true');
  const [mockFlag, setMockFlag] = useState<boolean>(localStorage.getItem('mockFlag') === 'true');
  const [typingMode, setTypingMode] = useState<boolean>(false);
  const [typedMessage, setTypedMessage] = useState<string>('');
  
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('typing')) {
      setTypingMode(true);
    }
  }, []);

  interface JetImage {
    src: string;
    category: string;
    hourlyPrice: number;
  }

  const urlParams = new URLSearchParams(window.location.search);
  const isMock = urlParams.has('mock');

  const mockJetResults = [
    {
      model: "Hawker 900XP",
      duration: "55 mins",
      seats: "9",
      cost: "$13,850",
      maxPax: "9"
    },
    {
      model: "Learjet 60XR",
      duration: "51 mins",
      seats: "7",
      cost: "$15,400",
      maxPax: "7"
    },
    {
      model: "Hawker 800XP",
      duration: "52 mins",
      seats: "9",
      cost: "$15,500",
      maxPax: "9"
    },
    {
      model: "Gulfstream G280",
      duration: "1 hr 10 mins",
      seats: "10",
      cost: "$25,000",
      maxPax: "10"
    },
    {
      model: "Cessna Citation X",
      duration: "1 hr 5 mins",
      seats: "12",
      cost: "$20,000",
      maxPax: "12"
    },
    {
      model: "Hawker 900XP",
      duration: "55 mins",
      seats: "9",
      cost: "$13,850",
      maxPax: "9"
    },
    {
      model: "Learjet 60XR",
      duration: "51 mins",
      seats: "7",
      cost: "$15,400",
      maxPax: "7"
    },
    {
      model: "Hawker 800XP",
      duration: "52 mins",
      seats: "9",
      cost: "$15,500",
      maxPax: "9"
    },
    {
      model: "Gulfstream G280",
      duration: "1 hr 10 mins",
      seats: "10",
      cost: "$25,000",
      maxPax: "10"
    },
    {
      model: "Cessna Citation X",
      duration: "1 hr 5 mins",
      seats: "12",
      cost: "$20,000",
      maxPax: "12"
    }
  ].map(jet => ({ ...jet, seats: jet.maxPax || jet.seats }));

  const [jetResults, setJetResults] = useState<any[]>(isMock ? mockJetResults : []);
  const [emblaRef, emblaApi] = useEmblaCarousel(OPTIONS);
  const [isJetResultsExpanded, setIsJetResultsExpanded] = useState(isMock);
  const [searchDetails, setSearchDetails] = useState({
    from: '',
    to: '',
    date: '',
    time: '',
    passengers: '',
  });

  useEffect(() => {
    if (isMock) {
      const today = new Date().toISOString().split('T')[0];
      setSearchDetails({
        from: 'KSFO',
        to: 'KLAX',
        date: today,
        time: '12:00',
        passengers: '2',
      });
      setContactForm({
        name: 'Johnny Test',
        phone: '123-456-7890',
        email: '',
        requestedJetNumber: '',
        from: 'SFO',
        to: 'LAX',
        date: today,
        time: '12:00',
        passengers: '2',
      });
      setMemoryKv({
        name: "Johnny Test",
        age: "40",
        location: "SF, CA",
        pets: "1 dog",
        common_to: "LA"
      });
    } else {
      setSearchDetails({
        from: '',
        to: '',
        date: '',
        time: '',
        passengers: '',
      });
    }
  }, [isMock]);
  
  useEffect(() => {
    setContactForm((prevForm) => ({
      ...prevForm,
      from: searchDetails.from,
      to: searchDetails.to,
      date: searchDetails.date,
      time: searchDetails.time,
      passengers: searchDetails.passengers,
    }));
  }, [searchDetails]);

  const [contactForm, setContactForm] = useState({
    name: '',
    phone: '',
    email: '',
    requestedJetNumber: '',
    from: '',
    to: '',
    date: '',
    time: '',
    passengers: '',
  });

  const handleContactFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const emailData = {
      secret: "yg67r58yogfibujhugiy6o78ftuhbk",
      to: "jacob@jet.ai",
      subject: `Booking Request: ${contactForm.to || ''} to ${contactForm.from || ''} ${contactForm.date || ''}`,
      text: `Name: ${contactForm.name || ''}\nPhone: ${contactForm.phone || ''}\nEmail: ${contactForm.email || ''}\nJet Requested: ${contactForm.requestedJetNumber || ''}\nFrom: ${contactForm.from || ''}\nTo: ${contactForm.to || ''}\nDate: ${contactForm.date || ''}\nTime: ${contactForm.time || ''}\nPassengers: ${contactForm.passengers || ''}`,
      html: `<p>Name: ${contactForm.name || ''}</p><p>Phone: ${contactForm.phone || ''}</p><p>Email: ${contactForm.email || ''}</p><p>Jet Requested: ${contactForm.requestedJetNumber || ''}</p><p>From: ${contactForm.from || ''}</p><p>To: ${contactForm.to || ''}</p><p>Date: ${contactForm.date || ''}</p><p>Time: ${contactForm.time || ''}</p><p>Passengers: ${contactForm.passengers || ''}</p>`,
      draft: false
    };

    try {
      const response = await fetch('https://jetleg.ai/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emailData),
      });

      if (!response.ok) {
        throw new Error('Failed to send email');
      }

      const result = await response.json();
      if (result.message === 'Email sent successfully' && result.response.ErrorCode === 0) {
        alert('Email sent successfully!');
      }
      setContactForm({
        name: '',
        phone: '',
        email: '',
        requestedJetNumber: '',
        from: '',
        to: '',
        date: '',
        time: '',
        passengers: '',
      });
    } catch (error) {
      console.error('Error sending email:', error);
    }
  };
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);


  const toggleJetResults = () => {
    setIsJetResultsExpanded((prev) => !prev);
  };

  useEffect(() => {
    const contentTop = document.querySelector('.content-top') as HTMLElement;
    if (contentTop) {
      contentTop.style.display = 'flex';
    }
  }, []);

  /**
   * Utility for formatting the timing of logs
   */
  const formatTime = useCallback((timestamp: string) => {
    const startTime = startTimeRef.current;
    const t0 = new Date(startTime).valueOf();
    const t1 = new Date(timestamp).valueOf();
    const delta = t1 - t0;
    const hs = Math.floor(delta / 10) % 100;
    const s = Math.floor(delta / 1000) % 60;
    const m = Math.floor(delta / 60_000) % 60;
    const pad = (n: number) => {
      let s = n + '';
      while (s.length < 2) {
        s = '0' + s;
      }
      return s;
    };
    return `${pad(m)}:${pad(s)}.${pad(hs)}`;
  }, []);

  /**
   * When you click the API key
   */
  const resetAPIKey = useCallback(() => {
    const apiKey = prompt('OpenAI API Key');
    if (apiKey !== null) {
      localStorage.clear();
      localStorage.setItem('tmp::voice_api_key', apiKey);
      window.location.reload();
    }
    const urlParams = new URLSearchParams(window.location.search);
    const isAdmin = urlParams.has('admin');
    const connectButton = document.querySelector('.connect-button');
    const toggleControl = document.querySelector('.toggle-control');

    if (connectButton) {
      (connectButton as HTMLButtonElement).style.height = isAdmin ? 'auto' : '0';
    }
    if (toggleControl) {
      (toggleControl as HTMLDivElement).style.opacity = isAdmin ? '1.0' : '0.0';
      (toggleControl as HTMLDivElement).style.height = isAdmin ? 'auto' : '0';
    }
  }, []);

  /**
   * Connect to conversation:
   * WavRecorder taks speech input, WavStreamPlayer output, client is API client
   */
  const connectConversation = useCallback(async () => {
    const client = clientRef.current;
    const wavRecorder = wavRecorderRef.current;
    const wavStreamPlayer = wavStreamPlayerRef.current;

    // Set state variables
    startTimeRef.current = new Date().toISOString();
    if (isConnected) return; // Prevent double connection
    setIsConnected(true);
    setRealtimeEvents([]);
    setItems(client.conversation.getItems());

    // Connect to microphone
    if (wavRecorder.getStatus() === 'ended' || wavRecorder.getStatus() === 'paused' || wavRecorder.getStatus() !== 'recording') {
      await wavRecorder.begin();
    }

    // Connect to audio output
    if (!wavStreamPlayer.context) {
      wavStreamPlayer.context = new AudioContext({ sampleRate: wavStreamPlayer.sampleRate });
    }
    await wavStreamPlayer.connect();

    // Connect to realtime API
    await client.connect();
    if (!isConnected) {
      client.sendUserMessageContent([
        {
          type: `input_text`,
          text: `Hello!`,
          // text: `For testing purposes, I want you to list ten car brands. Number each item, e.g. "one (or whatever number you are one): the item name".`
        },
      ]);
    }

    if (client.getTurnDetectionType() === 'server_vad') {
      await wavRecorder.record((data) => client.appendInputAudio(data.mono));
    }
  }, []);

  /**
   * Disconnect and reset conversation state
   */
  const disconnectConversation = useCallback(async () => {
    setIsConnected(false);
    setRealtimeEvents([]);
    setItems([]);
    setMemoryKv({});
    setCoords({
      lat: 37.775593,
      lng: -122.418137,
    });
    setMarker(null);

    const client = clientRef.current;
    client.disconnect();

    const wavRecorder = wavRecorderRef.current;
    await wavRecorder.end();

    const wavStreamPlayer = wavStreamPlayerRef.current;
    await wavStreamPlayer.interrupt();
  }, []);

  const deleteConversationItem = useCallback(async (id: string) => {
    const client = clientRef.current;
    client.deleteItem(id);
  }, []);

  /**
   * In push-to-talk mode, start recording
   * .appendInputAudio() for each sample
   */
  const startRecording = async () => {
    setIsRecording(true);
    const client = clientRef.current;
    const wavRecorder = wavRecorderRef.current;
    const wavStreamPlayer = wavStreamPlayerRef.current;
    const trackSampleOffset = await wavStreamPlayer.interrupt();
    if (trackSampleOffset?.trackId) {
      const { trackId, offset } = trackSampleOffset;
      await client.cancelResponse(trackId, offset);
    }
    await wavRecorder.record((data) => client.appendInputAudio(data.mono));
  };

  /**
   * In push-to-talk mode, stop recording
   */
  const stopRecording = async () => {
    setIsRecording(false);
    const client = clientRef.current;
    const wavRecorder = wavRecorderRef.current;
    await wavRecorder.pause();
    client.createResponse();
  };

  /**
   * Switch between Manual <> VAD mode for communication
   */
  const changeTurnEndType = async (value: string) => {
    const client = clientRef.current;
    const wavRecorder = wavRecorderRef.current;
    if (value === 'none' && wavRecorder.getStatus() === 'recording') {
      await wavRecorder.pause();
    }
    client.updateSession({
      turn_detection: value === 'none' ? null : { type: 'server_vad' },
    });
    if (value === 'server_vad' && client.isConnected()) {
      await wavRecorder.record((data) => client.appendInputAudio(data.mono));
    }
    setCanPushToTalk(value === 'none');
  };

  /**
   * Automatically connect if URL has ?auto and set up keypress listener for 'c'
   */
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'c') {
        connectConversation();
      }
    };

    window.addEventListener('keypress', handleKeyPress);

    if (urlParams.has('auto')) {
      setTimeout(() => {
        const buttons = document.querySelectorAll('button');
        const connectButton = Array.from(buttons).find(
          (button) => button.textContent?.toLowerCase() === 'connect'
        ) as HTMLButtonElement;
        if (connectButton) {
          connectButton.click();
        }
      }, 3000);
    }
    // Enable VAD mode if ?vad is present in the URL or vadFlag is true
    if (urlParams.has('vad') || vadFlag) {
      changeTurnEndType('server_vad');
    }

    // Automatically connect if ?auto is present in the URL or autoFlag is true
    if (urlParams.has('auto') || autoFlag) {
      setTimeout(() => {
        const buttons = document.querySelectorAll('button');
        const connectButton = Array.from(buttons).find(
          (button) => button.textContent?.toLowerCase() === 'connect'
        ) as HTMLButtonElement;
        if (connectButton) {
          connectButton.click();
        }
      }, 3000);
    }

    // Set mock state if ?mock is present in the URL or mockFlag is true
    if (urlParams.has('mock') || mockFlag) {
      setJetResults(mockJetResults);
      setIsJetResultsExpanded(true);
    }

    // Set display for connect and manual/vad buttons based on admin param
    const connectButton = document.querySelector('.connect-button');
    const toggleControl = document.querySelector('.toggle-control');
    if (urlParams.has('admin')) {
      if (connectButton) {
        (connectButton as HTMLButtonElement).style.display = 'block';
      }
      if (toggleControl) {
        (toggleControl as HTMLDivElement).style.display = 'block';
      }
    } else {
      if (connectButton) {
        (connectButton as HTMLButtonElement).style.display = 'none';
      }
      if (toggleControl) {
        (toggleControl as HTMLDivElement).style.display = 'none';
      }
    }

    window.addEventListener('keypress', handleKeyPress);

    return () => {
      window.removeEventListener('keypress', handleKeyPress);
    };
  }, [connectConversation]);

  /**
   * Auto-scroll the conversation logs
   */
  useEffect(() => {
    const conversationEls = [].slice.call(
      document.body.querySelectorAll('[data-conversation-content]')
    );
    for (const el of conversationEls) {
      const conversationEl = el as HTMLDivElement;
      conversationEl.scrollTop = conversationEl.scrollHeight;
    }
  }, [items]);

  /**
   * Set up render loops for the visualization canvas
   */
  useEffect(() => {
    let isLoaded = true;

    const wavRecorder = wavRecorderRef.current;
    const clientCanvas = clientCanvasRef.current;
    let clientCtx: CanvasRenderingContext2D | null = null;

    const wavStreamPlayer = wavStreamPlayerRef.current;
    const serverCanvas = serverCanvasRef.current;
    let serverCtx: CanvasRenderingContext2D | null = null;

    const render = () => {
      if (isLoaded) {
        if (clientCanvas) {
          if (!clientCanvas.width || !clientCanvas.height) {
            clientCanvas.width = clientCanvas.offsetWidth;
            clientCanvas.height = clientCanvas.offsetHeight;
          }
          clientCtx = clientCtx || clientCanvas.getContext('2d');
          if (clientCtx) {
            clientCtx.clearRect(0, 0, clientCanvas.width, clientCanvas.height);
            const result = wavRecorder.recording
              ? wavRecorder.getFrequencies('voice')
              : { values: new Float32Array([0]) };
            WavRenderer.drawBars(
              clientCanvas,
              clientCtx,
              result.values,
              '#0099ff',
              10,
              0,
              8
            );
          }
        }
        if (serverCanvas) {
          if (!serverCanvas.width || !serverCanvas.height) {
            serverCanvas.width = serverCanvas.offsetWidth;
            serverCanvas.height = serverCanvas.offsetHeight;
          }
          serverCtx = serverCtx || serverCanvas.getContext('2d');
          if (serverCtx) {
            serverCtx.clearRect(0, 0, serverCanvas.width, serverCanvas.height);
            const result = wavStreamPlayer.analyser
              ? wavStreamPlayer.getFrequencies('voice')
              : { values: new Float32Array([0]) };
            WavRenderer.drawBars(
              serverCanvas,
              serverCtx,
              result.values,
              '#009900',
              10,
              0,
              8
            );
          }
        }
        window.requestAnimationFrame(render);
      }
    };
    render();

    return () => {
      isLoaded = false;
    };
  }, []);

  /**
   * Core RealtimeClient and audio capture setup
   * Set all of our instructions, tools, events and more
   */
  useEffect(() => {
    // Get refs
    const wavStreamPlayer = wavStreamPlayerRef.current;
    const client = clientRef.current;

    // Set instructions
    client.updateSession({ instructions: instructions });
    // Set transcription, otherwise we don't get user transcriptions back
    client.updateSession({ input_audio_transcription: { model: 'whisper-1' } });

    // Add tools
    client.addTool(
      {
        name: 'toggle_drawer',
        description: 'Toggles the state of the drawer (open/close).',
        parameters: {
          type: 'object',
          properties: {},
        },
      },
      async () => {
        setIsJetResultsExpanded((prev) => !prev);
        return { message: 'Silent, not spoken: Drawer toggled successfully.' };
      }
    );

    client.addTool(
      {
        name: 'get_contact_info',
        description: 'Opens the contact information modal with provided details.',
        parameters: {
          type: 'object',
          properties: {
            name: {
              type: 'string',
              description: 'Name of the contact person',
            },
            phone: {
              type: 'string',
              description: 'Phone number of the contact person',
            },
            email: {
              type: 'string',
              description: 'Email address of the contact person (optional)',
            },
            requestedJetNumber: {
              type: 'number',
              description: 'Requested jet number from the search results (1-10, optional)',
            },
          },
          required: ['name', 'phone', 'requestedJetNumber'],
        },
      },
      async ({ name, phone, email, requestedJetNumber }: { name: string; phone: string; email?: string; requestedJetNumber: number }) => {
        setContactForm((prevForm) => ({
          ...prevForm,
          name,
          phone,
          email: email || '',
          requestedJetNumber: requestedJetNumber.toString(),
        }));
        setIsContactModalOpen(true);
        return { message: 'Contact info modal opened with provided details.' };
      }
    );
    const handleContactFormSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      const contactInfo = {
        name: contactForm.name,
        phone: contactForm.phone,
        email: contactForm.email,
        searchDetails,
        requestedJetNumber: contactForm.requestedJetNumber ? parseInt(contactForm.requestedJetNumber) : undefined,
      };
      console.log('Contact Info:', contactInfo);
      setContactForm({
        ...contactForm,
        from: searchDetails.from,
        to: searchDetails.to,
        date: searchDetails.date,
        time: searchDetails.time,
        passengers: searchDetails.passengers,
      });
      setIsContactModalOpen(true);
      console.log('Modal should show:', {
        from: searchDetails.from,
        to: searchDetails.to,
        date: searchDetails.date,
        time: searchDetails.time,
        passengers: searchDetails.passengers,
      });
    };
    client.addTool({
        name: 'go_to_slide',
        description: 'Navigates to a specific slide number (0-indexed internally, but shown as 1-indexed to users) by clicking the corresponding dot button.',
        parameters: {
          type: 'object',
          properties: {
            slideNumber: {
              type: 'number',
              description: 'The slide number to navigate to (0-indexed).',
            },
          },
          required: ['slideNumber'],
        },
      },
      async ({ slideNumber }: { [key: string]: any }) => {
        const dotButton = document.querySelector(`.embla-dot-${slideNumber}`);
        if (dotButton) {
          console.log(`Navigating to slide #${slideNumber + 1}`); // Show 1-indexed to users
          (dotButton as HTMLButtonElement).click();
          return { message: `Silent, not spoken: Navigated to slide #${slideNumber + 1} successfully.` }; // Show 1-indexed to users
        } else {
          console.log(`Slide #${slideNumber} not found.`);
          return { message: `Slide #${slideNumber} not found.` };
        }
      }
    );
    client.addTool({
        name: 'go_prev',
        description: 'Triggers the "Previous" button in the client application.',
        parameters: {
          type: 'object',
          properties: {},
        },
      },
      async () => {
        console.log('Attempting to click the "Previous" button.');
        const prevButton = document.querySelector('.embla__button--prev');
        if (prevButton) {
          console.log('Previous button found, attempting to click.');
          (prevButton as HTMLButtonElement).click();
          return { message: 'Silent, not spoken: Previous button triggered successfully.' };
        } else {
          console.log('Previous button not found. Available buttons:', document.querySelectorAll('button'));
          return { message: 'Previous button not found.' };
        }
      }
    );
    client.addTool({
        name: 'go_next',
        description: 'Triggers the "Next" button in the client application.',
        parameters: {
          type: 'object',
          properties: {},
        },
      },
      async () => {
        const nextButton = document.querySelector('.go-next-target');
        if (nextButton) {
          (nextButton as HTMLButtonElement).click();
          return { message: 'Silent, not spoken: Next button triggered successfully.' };
        } else {
          return { message: 'Next button triggered successfully.' };
        }
      }
    );

    
    
    // Add tools
client.addTool(
  {
    name: 'search_flights',
    description: 'Searches for flights.',
    parameters: {
      type: 'object',
      properties: {
        from: { type: 'string', description: 'Departure 4 letter ICAO airport code' },
        to: { type: 'string', description: 'Destination 4 letter ICAO airport code' },
        date: { type: 'string', description: 'Flight date in YYYY-MM-DD format' },
        passengers: { type: 'number', description: 'Number of passengers' },
        time: { type: 'string', description: 'Preferred departure time in HH:MM format' },
      },
      required: ['from', 'to', 'date', 'passengers', 'time'],
    },
  },
  async ({ from, to, date, passengers, time }: { [key: string]: any }) => {
    try {
      const response = await fetch('https://flycharterjet.com/api/ai-avinode-search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ from, to, date, time, pax: passengers }),
      });
      const data = await response.json();
      console.log('Received search details:', { from, to, date, time, passengers });
      const newSearchDetails = { from, to, date, time, passengers };
      console.log('Storing search details:', newSearchDetails);
      setSearchDetails(newSearchDetails);
      setContactForm((prevForm) => {
        const updatedForm = {
          ...prevForm,
          from: prevForm.from || (prevForm.from === '' ? newSearchDetails.from : prevForm.from),
          to: prevForm.to || (prevForm.to === '' ? newSearchDetails.to : prevForm.to),
          date: prevForm.date || (prevForm.date === '' ? newSearchDetails.date : prevForm.date),
          time: prevForm.time || (prevForm.time === '' ? newSearchDetails.time : prevForm.time),
          passengers: prevForm.passengers || (prevForm.passengers === '' ? newSearchDetails.passengers : prevForm.passengers),
        };
        console.log('Updating contact form with:', updatedForm);
        return updatedForm;
      });
      const simplifiedResults = data.data.results.map((result: any) => ({
        model: result.quote.lift.aircraftType,
        seats: result.quote.lift.maxPax,
        cost: formatCost(result.quote.sellerPrice.price),
        duration: formatDuration(result.quote.segments[0].flightMinutes),
        maxpax: result.quote.lift.maxPax,
      }));

      // Remove duplicate models
      const uniqueResults = simplifiedResults.reduce((acc: any[], current: any) => {
        const x = acc.find(item => item.model === current.model);
        if (!x) {
          acc.push(current);
        }
        return acc;
      }, []);
      setJetResults(uniqueResults.slice(0, 9));
      emblaApi?.scrollTo(0);
      const dotButton = document.querySelector('.embla-dot-0');
      if (dotButton) {
        (dotButton as HTMLButtonElement).click();
      }
      setIsJetResultsExpanded(true); // Open the jet results drawer
      return { results: uniqueResults, message: "Here's a flight for you!" };
    } catch (error: any) {
      console.error('Error fetching flight data:', error);
      return { error: error.message };
    }
  }
);
    
    client.addTool({
        name: 'set_memory',
        description: 'Saves important data about the user into memory.',
        parameters: {
          type: 'object',
          properties: {
            key: {
              type: 'string',
              description:
                'The key of the memory value. Always use lowercase and underscores, no other characters.',
            },
            value: {
              type: 'string',
              description: 'Value can be anything represented as a string',
            },
          },
          required: ['key', 'value'],
        },
      },
      async ({ key, value }: { [key: string]: any }) => {
        setMemoryKv((memoryKv) => {
          const newKv = { ...memoryKv };
          newKv[key] = value;
          return newKv;
        });
        return { ok: true };
      }
    );
    client.addTool(
      {
        name: 'get_weather',
        description:
          'Retrieves the weather for a given lat, lng coordinate pair. Specify a label for the location.',
        parameters: {
          type: 'object',
          properties: {
            lat: {
              type: 'number',
              description: 'Latitude',
            },
            lng: {
              type: 'number',
              description: 'Longitude',
            },
            location: {
              type: 'string',
              description: 'Name of the location',
            },
          },
          required: ['lat', 'lng', 'location'],
        },
      },
      async ({ lat, lng, location }: { [key: string]: any }) => {
        setMarker({ lat, lng, location });
        setCoords({ lat, lng, location });
        const result = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&current=temperature_2m,wind_speed_10m`
        );
        const json = await result.json();
        const temperature = {
          value: json.current.temperature_2m as number,
          units: json.current_units.temperature_2m as string,
        };
        const wind_speed = {
          value: json.current.wind_speed_10m as number,
          units: json.current_units.wind_speed_10m as string,
        };
        setMarker({ lat, lng, location, temperature, wind_speed });
        return json;
      }
    );


    // handle realtime events from client + server for event logging
    client.on('realtime.event', (realtimeEvent: RealtimeEvent) => {
      setRealtimeEvents((realtimeEvents) => {
        const lastEvent = realtimeEvents[realtimeEvents.length - 1];
        if (lastEvent?.event.type === realtimeEvent.event.type) {
          // if we receive multiple events in a row, aggregate them for display purposes
          lastEvent.count = (lastEvent.count || 0) + 1;
          return realtimeEvents.slice(0, -1).concat(lastEvent);
        } else {
          return realtimeEvents.concat(realtimeEvent);
        }
      });
    });
    client.on('error', (event: any) => console.error(event));
    client.on('conversation.interrupted', async () => {
      const trackSampleOffset = await wavStreamPlayer.interrupt();
      if (trackSampleOffset?.trackId) {
        const { trackId, offset } = trackSampleOffset;
        await client.cancelResponse(trackId, offset);
      }
    });
    client.on('conversation.updated', async ({ item, delta }: any) => {
      const items = client.conversation.getItems();
      if (delta?.audio) {
        wavStreamPlayer.add16BitPCM(delta.audio, item.id);
      }
      if (item.status === 'completed' && item.formatted.audio?.length) {
        const wavFile = await WavRecorder.decode(
          item.formatted.audio,
          24000,
          24000
        );
        item.formatted.file = wavFile;
      }
      setItems(items);
    });

    setItems(client.conversation.getItems());

    return () => {
      // cleanup; resets to defaults
      client.reset();
    };
  }, []);

  /**
   * Render the application
   */
  return (
    <div data-component="ConsolePage" className={`${isJetResultsExpanded ? 'jets-shown' : 'jets-hidden'} ${jetResults.length === 0 ? 'no-results' : ''}`}>
      <img src="/cgpt-logo-2.png" className="mini-logo" alt="Mini Logo" />
      <div className="content-top">
        <div className="content-title">
          <img src="/cgpt-logo-2.png" />
          <span>
            CharterGPT Realtime <br className="mobile-break" /><span className="demo-version">Demo V2</span>
          </span>
        </div>
        <div className="content-flags">
          <label>
            <input
              type="checkbox"
              checked={autoFlag}
              onChange={(e) => {
                setAutoFlag(e.target.checked);
                localStorage.setItem('autoFlag', e.target.checked.toString());
                const urlParams = new URLSearchParams(window.location.search);
                if (e.target.checked) {
                  urlParams.set('auto', 'true');
                  setVadFlag(false);
                  localStorage.setItem('vadFlag', 'false');
                  changeTurnEndType('none');
                  setVadFlag(false);
                  localStorage.setItem('vadFlag', 'false');
                  changeTurnEndType('none');
                  setVadFlag(false);
                  localStorage.setItem('vadFlag', 'false');
                  changeTurnEndType('none');
                  setVadFlag(false);
                  localStorage.setItem('vadFlag', 'false');
                  changeTurnEndType('none');
                  setVadFlag(false);
                  localStorage.setItem('vadFlag', 'false');
                  changeTurnEndType('none');
                  setVadFlag(false);
                  localStorage.setItem('vadFlag', 'false');
                  changeTurnEndType('none');
                  setMemoryKv({
                    name: "John",
                    age: "40",
                    location: "SF, CA",
                    pets: "1 dog",
                    common_to: "LA"
                  });
                  urlParams.delete('auto');
                }
                window.history.replaceState({}, '', `${window.location.pathname}?${urlParams}`);
                window.location.reload();
              }}
            />
            Auto
          </label>
          <label>
            <input
              type="checkbox"
              checked={vadFlag}
              onChange={(e) => {
                const isChecked = e.target.checked;
                setVadFlag(isChecked);
                localStorage.setItem('vadFlag', isChecked.toString());
                const urlParams = new URLSearchParams(window.location.search);
                if (isChecked) {
                  urlParams.set('vad', 'true');
                  urlParams.delete('typing');
                  setMemoryKv({
                    name: "John",
                    age: "40",
                    location: "SF, CA",
                    pets: "1 dog",
                    common_to: "LA"
                  });
                  const fakeMemories = {
                    name: "John",
                    age: "40",
                    location: "SF, CA",
                    pets: "1 dog",
                    common_to: "LA"
                  };
                } else {
                  setMemoryKv({});
                  localStorage.removeItem('memoryKv');
                  localStorage.removeItem('memoryKv');
                  urlParams.delete('vad');
                }
                window.history.replaceState({}, '', `${window.location.pathname}?${urlParams}`);
                window.location.reload();
              }}
            />
            <span>VAD</span>
          </label>
          <label>
            <input
              type="checkbox"
              checked={mockFlag}
              onChange={(e) => {
                setMockFlag(e.target.checked);
                localStorage.setItem('mockFlag', e.target.checked.toString());
                const urlParams = new URLSearchParams(window.location.search);
                if (e.target.checked) {
                  urlParams.set('mock', 'true');
                  if (fakeJets) {
                    setJetResults(mockJetResults);
                    setIsJetResultsExpanded(true);
                  }
                  if (fakeMems) {
                    const fakeMemories = {
                      name: "John",
                      age: "40",
                      location: "SF, CA",
                      pets: "1 dog",
                      common_to: "LA"
                    };
                    setMemoryKv(fakeMemories);
                    localStorage.setItem('memoryKv', JSON.stringify(fakeMemories));
                  }
                  if (fakeMems) {
                    const fakeMemories = {
                      name: "John",
                      age: "40",
                      location: "SF, CA",
                      pets: "1 dog",
                      common_to: "LA"
                    };
                    setMemoryKv(fakeMemories);
                    localStorage.setItem('memoryKv', JSON.stringify(fakeMemories));
                  } else {
                    setMemoryKv({});
                    localStorage.removeItem('memoryKv');
                  }
                } else {
                  setMemoryKv({});
                  localStorage.removeItem('memoryKv');
                  urlParams.delete('mock');
                }
                window.history.replaceState({}, '', `${window.location.pathname}?${urlParams}`);
                window.location.reload();
              }}
            />
            <span>Mock</span>
          </label>
          <label>
            <input
              type="checkbox"
              checked={typingMode}
              onChange={(e) => {
                const isChecked = e.target.checked;
                setTypingMode(isChecked);
                localStorage.setItem('typingMode', isChecked.toString());
                const urlParams = new URLSearchParams(window.location.search);
                if (isChecked) {
                  urlParams.set('typing', 'true');
                  urlParams.delete('vad');
                  setVadFlag(false);
                  localStorage.setItem('vadFlag', 'false');
                  changeTurnEndType('none');
                  setVadFlag(false);
                  localStorage.setItem('vadFlag', 'false');
                  changeTurnEndType('none');
                  setVadFlag(false);
                  localStorage.setItem('vadFlag', 'false');
                  changeTurnEndType('none');
                  setVadFlag(false);
                  localStorage.setItem('vadFlag', 'false');
                  changeTurnEndType('none');
                  setVadFlag(false);
                  localStorage.setItem('vadFlag', 'false');
                  changeTurnEndType('none');
                  setVadFlag(false);
                  localStorage.setItem('vadFlag', 'false');
                  changeTurnEndType('none');
                  urlParams.set('typing', 'true');
                  urlParams.delete('vad');
                } else {
                  urlParams.delete('typing');
                }
                window.history.replaceState({}, '', `${window.location.pathname}?${urlParams}`);
                window.location.reload();
              }}
            />
            <span>Typing Mode</span>
          </label>
        </div>
        <div className="content-api-key">
          {!LOCAL_RELAY_SERVER_URL && (
            <>
              <Button
                className="connect-button"
                icon={Edit}
                iconPosition="end"
                buttonStyle="flush"
                label={''}
                onClick={() => resetAPIKey()}
              />
            </>
          )}
        </div>
      </div>
      <div className="content-main">
        <Modal
          isOpen={isContactModalOpen}
          onRequestClose={() => setIsContactModalOpen(false)}
          contentLabel="Contact Form"
          className={`contact-modal ${isJetResultsExpanded ? 'drawer-open' : ''}`}
        >
          <button className="close-modal" onClick={() => setIsContactModalOpen(false)}>
            <X />
          </button>
          <h3>Contact Form</h3>
          <form onSubmit={handleContactFormSubmit}>
            <div className="input-row">
              <label>
                Name:
                <input
                  type="text"
                  placeholder="Name"
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  required
                />
              </label>
            </div>
            <div className="input-row">
              <label>
                Phone:
                <input
                  type="text"
                  placeholder="Phone"
                  value={contactForm.phone}
                  onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                  required
                />
              </label>
              <label>
                Email:
                <input
                  type="email"
                  placeholder="Email (optional)"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                />
              </label>
            </div>
            <div className="input-row">
              <label>
                Jet Requested:
                <input
                  type="number"
                  placeholder="Requested Jet Number"
                  value={contactForm.requestedJetNumber}
                  onChange={(e) => setContactForm({ ...contactForm, requestedJetNumber: e.target.value })}
                />
              </label>
              <label>
                Passengers*:
                <input
                  type="number"
                  placeholder="Passengers"
                  value={contactForm.passengers}
                  onChange={(e) => setContactForm({ ...contactForm, passengers: e.target.value })}
                  required
                />
              </label>
            </div>
            <div className="input-row">
              <label>
                From:
                <input
                  type="text"
                  placeholder="From"
                  value={contactForm.from}
                  onChange={(e) => setContactForm({ ...contactForm, from: e.target.value })}
                  required
                />
              </label>
              <label>
                To:
                <input
                  type="text"
                  placeholder="To"
                  value={contactForm.to}
                  onChange={(e) => setContactForm({ ...contactForm, to: e.target.value })}
                  required
                />
              </label>
            </div>
            <div className="input-row">
              <input
                type="date"
                placeholder="Date"
                value={contactForm.date}
                onChange={(e) => setContactForm({ ...contactForm, date: e.target.value })}
                required
              />
              <input
                type="time"
                placeholder="Time"
                value={contactForm.time}
                onChange={(e) => setContactForm({ ...contactForm, time: e.target.value })}
                required
              />
            </div>
            <button className="contact-submit-button" type="submit" style={{ minWidth: '250px' }}>Submit</button>
          </form>
        </Modal>
        <div className="content-logs">
          <div className="content-block conversation">
            <div className="content-block-title">conversation</div>
            <div className="content-block-body" data-conversation-content>
              {!items.length && `awaiting connection...`}
              {items.filter(item => item.type !== 'function_call_output').map((conversationItem, i) => {
                return (
                  <div className="conversation-item" key={conversationItem.id}>
                    <div className={`speaker ${conversationItem.role || ''}`}>
                      <div>
                        {(
                          conversationItem.role || conversationItem.type
                        ).replaceAll('_', ' ')}
                      </div>
                      <div
                        className="close"
                        onClick={() =>
                          deleteConversationItem(conversationItem.id)
                        }
                      >
                        <X />
                      </div>
                    </div>
                    <div className={`speaker-content ${conversationItem.role}`}>
                      <div>
                        {/* tool call */}
                        {!!conversationItem.formatted.tool && (
                          <div>
                            {conversationItem.formatted.tool.name}(
                            {conversationItem.formatted.tool.name === 'go_to_slide' ? 
                              (() => {
                                try {
                                  const args = JSON.parse(conversationItem.formatted.tool.arguments);
                                  return `slideNumber: ${args.slideNumber + 1}`;
                                } catch (error) {
                                  console.error('Error parsing tool arguments:', error);
                                  return conversationItem.formatted.tool.arguments;
                                }
                              })() : 
                              conversationItem.formatted.tool.arguments})
                          </div>
                        )}
                        {!conversationItem.formatted.tool &&
                          conversationItem.role === 'user' && (
                            <div
                              dangerouslySetInnerHTML={{
                                __html: formatText(
                                  conversationItem.formatted.transcript ||
                                    (conversationItem.formatted.audio?.length
                                      ? '(awaiting transcript)'
                                      : conversationItem.formatted.text ||
                                        '(item sent)')
                                ),
                              }}
                            />
                          )}
                        {!conversationItem.formatted.tool &&
                          conversationItem.role === 'assistant' && (
                            <div
                              dangerouslySetInnerHTML={{
                                __html: formatText(
                                  (conversationItem.formatted.transcript ||
                                    conversationItem.formatted.text ||
                                    '(truncated)').replace(
                                    /\[([^\]]+)\]\((https?:\/\/[^\s]+)\)/g,
                                    '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
                                  )
                                ),
                              }}
                            />
                          )}
                        {conversationItem.formatted.file && (
                          <audio
                            src={conversationItem.formatted.file.url}
                            controls
                          />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="content-actions">
            {typingMode ? (
              <div className="typing-mode centered">
                <input
                  type="text"
                  value={typedMessage}
                  onChange={(e) => setTypedMessage(e.target.value)}
                  placeholder="Type your message here..."
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter') {
                      const client = clientRef.current;
                      const wavRecorder = wavRecorderRef.current;
                      if (wavRecorder.getStatus() === 'recording') {
                        await wavRecorder.end(); // Ensure the session is ended before sending
                      }
                      if (!client.isConnected()) {
                        await client.connect();
                      }
                      client.sendUserMessageContent([
                        {
                          type: `input_text`,
                          text: typedMessage,
                        },
                      ]);
                      setTypedMessage('');
                    }
                  }}
                  className="typing-input"
                  onFocus={() => {
                    if (window.innerWidth <= 640 && isJetResultsExpanded) {
                      setIsJetResultsExpanded(false);
                    }
                  }}
                />
                <Button
                  label="Send"
                  buttonStyle="regular"
                  onClick={async () => {
                    const client = clientRef.current;
                    const wavRecorder = wavRecorderRef.current;
                    const wavStreamPlayer = wavStreamPlayerRef.current;

                    // Interrupt any ongoing audio stream
                    const trackSampleOffset = await wavStreamPlayer.interrupt();
                    if (trackSampleOffset?.trackId) {
                      const { trackId, offset } = trackSampleOffset;
                      await client.cancelResponse(trackId, offset);
                    }

                    // Ensure the session is ended before sending
                    if (wavRecorder.getStatus() === 'recording') {
                      await wavRecorder.end();
                    }

                    // Send the new message
                    client.sendUserMessageContent([
                      {
                        type: `input_text`,
                        text: typedMessage,
                      },
                    ]);
                    setTypedMessage('');
                  }}
                  style={{ minWidth: '250px' }}
                />
              </div>
            ) : (
              <>
                <Toggle
                  defaultValue={false}
                  labels={['manual', 'vad']}
                  values={['none', 'server_vad']}
                  onChange={(_, value) => changeTurnEndType(value)}
                />
                <div className="spacer" />
                {isConnected && canPushToTalk && (
                  <div className="push-to-talk-wrapper">
                    <Button
                      label={isRecording ? 'release to send' : 'push to talk'}
                      buttonStyle={isRecording ? 'alert' : 'regular'}
                      disabled={!isConnected || !canPushToTalk}
                      onMouseDown={startRecording}
                      onMouseUp={stopRecording}
                      onTouchStart={startRecording}
                      onTouchEnd={stopRecording}
                      style={{ minWidth: '250px' }}
                    />
                  </div>
                )}
                <div className="spacer" />
              </>
            )}
            <div className="connect-disconnect-wrapper">
              <Button
                className="connect-disconnect-button"
                label={isConnected ? 'disconnect' : 'connect'}
                iconPosition={isConnected ? 'end' : 'start'}
                icon={isConnected ? X : Zap}
                buttonStyle={isConnected ? 'regular' : 'action'}
                onClick={
                  isConnected ? disconnectConversation : connectConversation
                }
              />
            </div>
          </div>
        </div>
        <div className="content-right">
          {marker && (
            <div className="content-block map">
              <div className="content-block-title">get_weather()</div>
              <div className="content-block-title bottom">
                {marker.location}
                {!!marker.temperature && (
                  <>
                    <br />
                    🌡️ {marker.temperature.value} {marker.temperature.units}
                  </>
                )}
                {!!marker.wind_speed && (
                  <>
                    {' '}
                    🍃 {marker.wind_speed.value} {marker.wind_speed.units}
                  </>
                )}
              </div>
              <div className="content-block-body full">
                <Map
                  center={[marker.lat, marker.lng]}
                  location={marker.location}
                />
              </div>
            </div>
          )}
          <div className={`content-block phrases-section ${phrasesExpanded ? 'expanded' : 'collapsed'}`}>
            <div className="content-block-title" onClick={() => setPhrasesExpanded(!phrasesExpanded)}>
              Suggested Phrases {phrasesExpanded ? '▲' : '▼'}
            </div>
            {phrasesExpanded && (
              <div className="content-block-body">
                <ul>
                  <li>"Hello"</li>
                  <li>"What can you do?"</li>
                  <li>"Search for flights to/from on..."</li>
                  <li>"Suggest a trip for me..."</li>
                  <li>"Toggle Drawer"</li>
                  <li>"Next Slide"</li>
                  <li>"Show me Jet Slide 4"</li>
                  <li>"Request Callback..."</li>
                  <li>"My contact info is..."</li>
                  <li>"Request booking..."</li>
                  <li>"Remember I am..."</li>
                </ul>
              </div>
            )}
          </div>
          {Object.keys(memoryKv).length > 0 && (
            <div className={`content-block kv memory-section ${isMemoryExpanded ? 'expanded' : 'collapsed'}`}>
              <div className="content-block-title" onClick={() => setIsMemoryExpanded(!isMemoryExpanded)}>
                set_memory({Object.keys(memoryKv).length}) {isMemoryExpanded ? '▲' : '▼'}
              </div>
              {isMemoryExpanded && (
                <div className={`content-block-body content-kv ${isMemoryExpanded ? '' : 'collapsed'}`}>
                  {JSON.stringify(memoryKv, null, 2)}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <div className={`jet-results-section ${isJetResultsExpanded ? 'expanded' : ''} ${jetResults.length === 0 ? 'no-results' : ''}`}>
        <div className="jet-results-header" onClick={toggleJetResults}>
          Jet Results {Math.min(jetResults.length, 9)} {isJetResultsExpanded ? '▲' : '▼'}
        </div>
        {isJetResultsExpanded && jetResults.length === 0 && (
          <div className="no-results-message">
            No results yet, search for a flight first or try again.
          </div>
        )}
        {isJetResultsExpanded && jetResults.length > 0 && (
          <EmblaCarousel cards={jetResults.slice(0, 9)} options={OPTIONS} />
        )}
      </div>
    </div>
  );
}
